import { Controller } from "@hotwired/stimulus"

// Stripe Elements + payment confirmation for job transaction purchases.
export default class extends Controller {
  static values = {
    url: String,
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.boundStripeSubmit = this.handleStripeSubmit.bind(this)
    this.boundChargeToggled = this.onChargeToggled.bind(this)
    this.stripeElements = null
    this.stripeCard = null
    this.stripeFormSubmit = null
    this.stripeSubmitAttached = false

    this.element.addEventListener("purchase-form:chargeToggled", this.boundChargeToggled)

    if (document.getElementById("vendors-stripe-elements")) {
      void this.initStripe()
    }
  }

  disconnect() {
    this.removeStripeSubmitListener()
    this.element.removeEventListener("purchase-form:chargeToggled", this.boundChargeToggled)

    if (this.stripeCard) {
      this.stripeCard.unmount()
      this.stripeCard = null
    }
  }

  onChargeToggled(event) {
    if (event.detail.charge) {
      this.addStripeSubmitListener()
    } else {
      this.removeStripeSubmitListener()
    }
  }

  addStripeSubmitListener() {
    if (this.stripeSubmitAttached || !this.element) return

    this.element.addEventListener("submit", this.boundStripeSubmit)
    this.stripeSubmitAttached = true
  }

  removeStripeSubmitListener() {
    if (!this.stripeSubmitAttached || !this.element) return

    this.element.removeEventListener("submit", this.boundStripeSubmit)
    this.stripeSubmitAttached = false
  }

  async initStripe() {
    const stripeElementsEl = document.getElementById("vendors-stripe-elements")
    if (!stripeElementsEl || typeof stripe === "undefined") return

    const stripeForm = stripeElementsEl.closest("form")
    if (!stripeForm) return

    const handledRedirect = await this.handleStripeRedirectReturn(stripeForm)
    if (handledRedirect) return

    this.stripeElements = stripe.elements({
      amount: 10000,
      currency: "usd",
      mode: "payment",
    })
    this.stripeCard = this.stripeElements.create("payment")
    this.stripeFormSubmit = stripeForm.querySelector('button[type="submit"]')

    this.stripeCard.mount(`#${stripeElementsEl.id}`)
  }

  async handleStripeRedirectReturn(stripeForm) {
    const returnParams = new URLSearchParams(window.location.search)
    const redirectClientSecret = returnParams.get("payment_intent_client_secret")
    const redirectStatus = returnParams.get("redirect_status")

    if (
      !redirectClientSecret ||
      redirectStatus !== "succeeded" ||
      stripeForm.querySelector('input[name="stripeToken"]')
    ) {
      return false
    }

    const { paymentIntent, error: retrieveError } =
      await stripe.retrievePaymentIntent(redirectClientSecret)

    if (
      retrieveError ||
      !paymentIntent ||
      paymentIntent.status !== "succeeded"
    ) {
      return false
    }

    const url = new URL(window.location.href)
    url.searchParams.delete("payment_intent")
    url.searchParams.delete("payment_intent_client_secret")
    url.searchParams.delete("redirect_status")
    window.history.replaceState({}, "", url.pathname + url.search + url.hash)

    this.appendStripeTokenAndSubmit(stripeForm, paymentIntent.id)
    return true
  }

  async handleStripeSubmit(event) {
    event.preventDefault()

    if (!this.stripeElements || !this.stripeFormSubmit) return

    this.stripeFormSubmit.disabled = true

    const newCardElement = document.querySelector(
      "#job_transaction_selected_payment_method_-1"
    )
    const newCard = !newCardElement || newCardElement.checked

    if (newCard) {
      const { error: submitError } = await this.stripeElements.submit()
      if (submitError) {
        this.handleStripeError(submitError)
        return
      }
    }

    const categoryInputs = this.element.querySelectorAll(
      '[data-purchase-form-target="category"]:checked'
    )
    const checkedCategories = Array.from(categoryInputs).map((element) => element.value)

    const response = await fetch(this.urlValue, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
      method: "POST",
      body: JSON.stringify({ payment: { items: checkedCategories } }),
    })
    const { clientSecret } = await response.json()

    let error
    let paymentIntent

    if (newCard) {
      ;({ error, paymentIntent } = await stripe.confirmPayment({
        clientSecret,
        elements: this.stripeElements,
        confirmParams: {
          return_url: this.stripeReturnUrl(),
        },
        redirect: "if_required",
      }))
    } else {
      const selectedRadioButton = document.querySelector(
        '[name="job_transaction[selected_payment_method]"]:checked'
      )
      const firstPaymentMethod = document.querySelector(
        '[name="job_transaction[selected_payment_method]"]'
      )
      const paymentMethodId =
        (selectedRadioButton && selectedRadioButton.value) ||
        (firstPaymentMethod && firstPaymentMethod.value)

      ;({ error, paymentIntent } = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          payment_method: paymentMethodId,
          return_url: this.stripeReturnUrl(),
        },
        redirect: "if_required",
      }))
    }

    if (error) {
      this.handleStripeError(error)
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      this.hideStripeErrors()
      this.appendStripeTokenAndSubmit(this.element, paymentIntent.id)
    }
  }

  handleStripeError(error) {
    if (this.stripeFormSubmit) {
      this.stripeFormSubmit.disabled = false
    }

    const cardErrors = document.getElementById("card-selector-errors")
    const stripeElementsErrors = document.getElementById(
      "vendors-stripe-elements-errors"
    )

    if (cardErrors) {
      cardErrors.textContent =
        "There was an error processing your payment. Please try again."
      cardErrors.classList.remove("hidden")
    }

    if (stripeElementsErrors) {
      stripeElementsErrors.textContent = error.message
      stripeElementsErrors.classList.remove("hidden")
    }
  }

  hideStripeErrors() {
    document.getElementById("card-selector-errors")?.classList.add("hidden")
    document.getElementById("vendors-stripe-elements-errors")?.classList.add("hidden")
  }

  appendStripeTokenAndSubmit(form, tokenId) {
    const stripeHiddenInput = document.createElement("input")
    stripeHiddenInput.setAttribute("type", "hidden")
    stripeHiddenInput.setAttribute("name", "stripeToken")
    stripeHiddenInput.setAttribute("value", tokenId)
    form.appendChild(stripeHiddenInput)
    form.submit()
  }

  stripeReturnUrl() {
    return window.location.href.split("#")[0]
  }
}
